/** @file projectJoints: every visible joint of a pose on screen (target pixels) with its view depth. */
import type { Vec3 } from '@zilo/pose3d-math/vec3/vec3';
import type { PoseBuffer } from '@zilo/pose3d-pose/contract';
import { projectOffset } from '@zilo/pose3d-render-core/camera/projectOffset';
import { screenOf } from '@zilo/pose3d-render-core/camera/screenOf';
import type { View } from '@zilo/pose3d-render-core/camera/viewOf';
import type { V3 } from './matrixRows';
import { pictureWorldPoint } from './pictureWorldPoint';
import { poseNode } from './poseNode';

/** One projected joint: its name, pose index, screen point and depth (units, growing away from the camera). */
export type ScreenJoint = { name: string; index: number; x: number; y: number; depth: number };

/**
 * The world point `entityPos + pose position` of each node (order = `Object.keys(puppet.nodes)`, the pose order)
 * through render-core's `screenOf`; hidden nodes are left out. With `fracs` (`jointFracs`, two per node) the position
 * is first placed where the bone marks draw it (`pictureWorldPoint`), so a pick lands on the drawn joint.
 */
export function projectJoints(pose: PoseBuffer, puppet: { nodes: Record<string, unknown> }, view: View, entityPos: Vec3 = [0, 0, 0], fracs?: ArrayLike<number>): ScreenJoint[] {
  const at: V3 = [0, 0, 0];
  return Object.keys(puppet.nodes).flatMap((name, index): ScreenJoint[] => {
    const node = poseNode(pose, index);
    if (node.hidden) return [];
    if (fracs === undefined) [at[0], at[1], at[2]] = node.pos;
    else pictureWorldPoint(at, node.pos, view, fracs[2 * index] ?? 0.5, fracs[2 * index + 1] ?? 0.5);
    const p: Vec3 = [entityPos[0] + at[0], entityPos[1] + at[1], entityPos[2] + at[2]];
    const [x, y] = screenOf(p, view);
    const depth = projectOffset([p[0] - view.target[0], p[1] - view.target[1], p[2] - view.target[2]], view)[2];
    return [{ name, index, x, y, depth }];
  });
}
